import { useMutation } from "@tanstack/react-query";
import { useState } from "react";
import { useTranslation } from "react-i18next";
import { trpc } from "../../../app/trpc.js";
import { Card, Input, Label, Notice } from "../../../ui/index.js";
import { StepNav } from "../StepNav.js";
import type { StepProps } from "../types.js";
import { useUnsavedChanges } from "../unsaved.js";

/** Код шаблона «Другое» (как `OTHER_TEMPLATE` на сервере): свой тип салона, пустой прайс, только общие вопросы. */
const OTHER = "other";

/**
 * Шаг 1: ниша. Шаблон подставляет черновик услуг с ориентировочными ценами и частые вопросы, дальше владелец их правит.
 * Если ниши нет в списке, «Другое» со своим названием типа (решение 04.11.2026).
 */
export function TemplateStep({ salonId, state, setState, next, back }: StepProps) {
  const { t } = useTranslation();
  const [code, setCode] = useState(state.templateCode ?? "barbershop");
  const [nicheName, setNicheName] = useState(state.nicheName ?? "");
  const other = code === OTHER;
  useUnsavedChanges(code !== (state.templateCode ?? "barbershop") || (other && nicheName.trim() !== (state.nicheName ?? "")));
  const choose = useMutation(
    trpc.onboarding.chooseTemplate.mutationOptions({
      onSuccess: (s) => {
        setState(s);
        next({ saved: true });
      },
    }),
  );
  const onNext = () => {
    // Другой шаблон заменяет черновик услуг; тот же оставляет правки владельца.
    const replaceDraft = state.templateCode !== null && state.templateCode !== code;
    choose.mutate({ salonId, code, replaceDraft, ...(other ? { nicheName: nicheName.trim() } : {}) });
  };
  return (
    <div>
      <p className="mb-4 text-slate-600">{t("onb.template.intro")}</p>
      <fieldset className="min-w-0 space-y-2" aria-label={t("onb.template.choose")}>
        {state.templates.map((tpl) => (
          <button key={tpl.code} type="button" aria-pressed={code === tpl.code} onClick={() => setCode(tpl.code)} className="block w-full text-left">
            <Card className={code === tpl.code ? "border-slate-900 ring-2 ring-slate-200" : "hover:border-slate-400"}>
              <div className="font-medium">{t(`onb.niche.${tpl.code}`)}</div>
              <div className="text-sm text-slate-500">
                {tpl.code === OTHER
                  ? t("onb.template.otherHint")
                  : tpl.servicesCount > 0
                    ? t("onb.template.services", { count: tpl.servicesCount })
                    : t("onb.template.empty")}
              </div>
            </Card>
          </button>
        ))}
      </fieldset>
      {other && (
        <div className="mt-3">
          <Label htmlFor="niche-name">{t("onb.template.otherName")}</Label>
          <Input
            id="niche-name"
            value={nicheName}
            maxLength={60}
            onChange={(e) => setNicheName(e.target.value)}
            placeholder={t("onb.template.otherPlaceholder")}
            autoComplete="off"
          />
        </div>
      )}
      {state.templateCode && state.templateCode !== code && <Notice>{t("onb.template.replaceWarning")}</Notice>}
      {choose.isError && <Notice kind="error">{t("common.error")}</Notice>}
      <StepNav onBack={back} onNext={onNext} disabled={other && !nicheName.trim()} busy={choose.isPending} />
    </div>
  );
}
